import React, { useRef, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import LockOutlineIcon from "@mui/icons-material/LockOutline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CloseIcon from "@mui/icons-material/Close";
import ContinueButton from "./ContinueButton";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Pro_Icon from "@/_images/speedicons/Pro_Icon.svg";
import Copilot_Icon from "@/_images/speedicons/Copilot_Icon.svg";
import RTSpeedLimiter_Icon from "@/_images/speedicons/RTSpeedLimiter_Icon.svg";
import Config_Icon from "@/_images/speedicons/Config_Icon.svg";
import AccelAndRevControl_Icon from "@/_images/speedicons/AccelAndRevControl_Icon.svg";
import GoalDriven_Icon from "@/_images/speedicons/GoalDriven_Icon.svg";
import ReportCard_Icon from "@/_images/speedicons/ReportCard_Icon.svg";
import CustService_Icon from "@/_images/speedicons/CustService_Icon.svg";
import FreeInstall_Icon from "@/_images/speedicons/FreeInstall_Icon.svg";
import BeatLoader from "react-spinners/BeatLoader";
import { connect } from "react-redux";
import { loadIsaEnrollmentCodeByState } from "../../_redux/actions/enrolleeActions";
import Cookies from "universal-cookie";
import { LifeSafer } from "../../_helpers/BrandConstants";

const PricingPlans = ({
  submitting,
  goToBackStep,
  handleUpdate,
  loadIsaEnrollmentCodeByState,
  onEnrollmentCodeError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const scrollRef = useRef(null);
  const [openDialog, setOpenDialog] = useState(null);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(true);
  const { t } = useTranslation();
  const cookies = new Cookies();
  const [loadingPlanIndex, setLoadingPlanIndex] = useState(null);
  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser"));
  const brand = cookies.get("lsEnrollmentReferral") || LifeSafer;

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowPrev(scrollLeft > 0);
      setShowNext(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  useEffect(() => {
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => ref.removeEventListener("scroll", handleScroll);
    }
  }, []);

  useEffect(() => {
    handleScroll();
  }, [isMobile]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.offsetWidth - 50;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -cardWidth : cardWidth,
        behavior: "smooth",
      });
    }
  };

  const plans = [
    {
      name: t("speed.pro"),
      save: "40%",
      icon: Pro_Icon,
      price: t("speed.billedMonthly1"),
      feeGroupName: "Speed Pro Nov 2025 DP",
      guarantee: t("speed.priceLockGuarantee1") + "**",
      features: [
        { label: t("speed.realTimeSpeed"), icon: RTSpeedLimiter_Icon },
        { label: t("speed.configurableTopLimits"), icon: Config_Icon },
        {
          label: t("speed.accelerationReverseControl"),
          icon: AccelAndRevControl_Icon,
        },
        { label: t("speed.goalTraining"), icon: GoalDriven_Icon },
        { label: t("speed.driverReport"), icon: ReportCard_Icon },
        { label: t("speed.dedicatedCustomerService"), icon: CustService_Icon },
        { label: t("speed.freeInstallation") + "*", icon: FreeInstall_Icon },
      ],
      details: [
        {
          title: t("speed.realTimeSpeed"),
          text: t("speed.realTimeSpeedDescription"),
        },
        {
          title: t("speed.configurabletTopSpeedLimit"),
          text: t("speed.configurabletTopSpeedLimitDescription"),
        },
        {
          title: t("speed.accelerationReverseControl"),
          text: t("speed.accelerationReverseControlDescription"),
        },
        {
          title: t("speed.goalTraining"),
          text: t("speed.goalTrainingDescription"),
        },
        {
          title: t("speed.driverReport"),
          text: t("speed.driverReportDescription"),
        },
        {
          title: t("speed.dedicatedCustomerService"),
          text: t("speed.dedicatedCustomerServiceDescription"),
        },
        {
          title: t("speed.freeInstallationCost") + "*",
          text: t("speed.freeInstallationCostDescription"),
        },
        {
          title: t("speed.priceLockGuarantee") + "**",
          text: t("speed.priceLockGuaranteeDescription"),
        },
      ],
    },
    {
      name: t("speed.copilot"),
      save: "25%",
      icon: Copilot_Icon,
      price: t("speed.billedMonthly2"),
      feeGroupName: "Speed Copilot Nov 2025 DP",
      guarantee: t("speed.priceLockGuarantee2") + "**",
      features: [
        { label: t("speed.realTimeSpeed"), icon: RTSpeedLimiter_Icon },
        { label: t("speed.configurableTopLimits"), icon: Config_Icon },
        {
          label: t("speed.accelerationReverseControl"),
          icon: AccelAndRevControl_Icon,
        },
        { label: t("speed.goalTraining"), icon: GoalDriven_Icon },
        { label: t("speed.driverReport"), icon: ReportCard_Icon },
        { label: t("speed.dedicatedCustomerService"), icon: CustService_Icon },
        { label: t("speed.freeInstallation") + "*", icon: FreeInstall_Icon },
      ],
      details: [
        {
          title: t("speed.realTimeSpeed"),
          text: t("speed.realTimeSpeedDescription"),
        },
        {
          title: t("speed.configurabletTopSpeedLimit"),
          text: t("speed.configurabletTopSpeedLimitDescription"),
        },
        {
          title: t("speed.accelerationReverseControl"),
          text: t("speed.accelerationReverseControlDescription"),
        },
        {
          title: t("speed.goalTraining"),
          text: t("speed.goalTrainingDescription"),
        },
        {
          title: t("speed.driverReport"),
          text: t("speed.driverReportDescription"),
        },
        {
          title: t("speed.dedicatedCustomerService"),
          text: t("speed.dedicatedCustomerServiceDescription"),
        },
        {
          title: t("speed.freeInstallationCost") + "*",
          text: t("speed.freeInstallationCostDescription"),
        },
        {
          title: t("speed.priceLockGuarantee") + "**",
          text: t("speed.priceLockGuaranteeDescription2"),
        },
      ],
    },

    // {
    //   name: t("speed.kickstart"),
    //   save: "",
    //   icon: Kickstart_Icon,
    //   price: t("speed.billedMonthly3"),
    //   feeGroupName: "Speed Kickstart Nov 2025 DP",
    //   guarantee: t("speed.priceLockGuarantee3"),
    //   features: [
    //     { label: t("speed.realTimeSpeed"), icon: RTSpeedLimiter_Icon },
    //     { label: t("speed.configurableTopLimits"), icon: Config_Icon },
    //     {
    //       label: t("speed.accelerationReverseControl"),
    //       icon: AccelAndRevControl_Icon,
    //     },
    //     { label: t("speed.goalTraining"), icon: GoalDriven_Icon },
    //     { label: t("speed.driverReport"), icon: ReportCard_Icon },
    //     { label: t("speed.dedicatedCustomerService"), icon: CustService_Icon },
    //   ],
    //   details: [
    //     {
    //       title: t("speed.realTimeSpeed"),
    //       text: t("speed.realTimeSpeedDescription"),
    //     },
    //     {
    //       title: t("speed.configurabletTopSpeedLimit"),
    //       text: t("speed.configurabletTopSpeedLimitDescription"),
    //     },
    //     {
    //       title: t("speed.accelerationReverseControl"),
    //       text: t("speed.accelerationReverseControlDescription"),
    //     },
    //     {
    //       title: t("speed.goalTraining"),
    //       text: t("speed.goalTrainingDescription"),
    //     },
    //     {
    //       title: t("speed.driverReport"),
    //       text: t("speed.driverReportDescription"),
    //     },
    //     {
    //       title: t("speed.dedicatedCustomerService"),
    //       text: t("speed.dedicatedCustomerServiceDescription"),
    //     },
    //     {
    //       title: t("speed.priceLockGuarantee") + "**",
    //       text: t("speed.priceLockGuaranteeDescription3"),
    //     },
    //   ],
    // },
  ];

  const handleOpenDialog = (plan) => setOpenDialog(plan);
  const handleCloseDialog = () => setOpenDialog(null);

  const handleSelectPlan = async (plan, index) => {
    setLoadingPlanIndex(index);
    try {
      const response = await loadIsaEnrollmentCodeByState(
        enrolleeUser?.state || enrolleeUser?.State,
        brand,
      );
      let selectedEnrollment = null;
      if (Array.isArray(response)) {
        selectedEnrollment = response.find(
          // (item) => item?.feeGroupName === plan?.feeGroupName
          (item) =>
            item?.feeGroupName?.trim().toLowerCase() ===
            plan?.feeGroupName?.trim().toLowerCase(),
        );
      }
      if (selectedEnrollment?.enrollmentCodeId) {
        cookies.set("lsEnrollmentCode", selectedEnrollment, {
          path: "/",
          sameSite: "strict",
        });
        const updateData = {
          enrollmentCodeId: selectedEnrollment?.enrollmentCodeId,
        };
        handleUpdate("Pricing", updateData);
      } else {
        handleUpdate("Pricing");
      }
    } catch (error) {
      console.error("Error validating code", error);
      let errorDetails = error?.message;
      try {
        errorDetails = JSON.parse(error?.message);
      } catch {
        // Use the original message when the response body is not JSON.
      }
      const errorStatus =
        error?.status || error?.response?.status || errorDetails?.status;

      if ([400, 404].includes(errorStatus) && onEnrollmentCodeError) {
        try {
          await onEnrollmentCodeError({
            errorDetails,
            errorStatus,
            selectedPlan: plan,
            brandIdentifier: brand,
          });
        } catch (loggingError) {
          console.error("Error logging general exception", loggingError);
        }
      }
      handleUpdate("Pricing");
    } finally {
      setLoadingPlanIndex(null);
    }
  };

  return (
    <Box
      sx={{
        mt: "25px",
        width: "100%",
        position: "relative",
        overflow: "auto",
        mx: "auto",
      }}
    >
      {isMobile && showPrev && (
        <IconButton
          onClick={() => scroll("left")}
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            width: 32,
            height: 32,
            background: "rgba(255,255,255,0.9)",
            "&:hover": { background: "rgba(255,255,255,1)" },
          }}
        >
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
      )}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: isMobile ? 1 : 4,
          marginBottom: isMobile ? "25px" : "25px",
        }}
      >
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            gap: isMobile ? 1 : 1,
            overflowX: isMobile ? "auto" : "visible",
            scrollSnapType: isMobile ? "x mandatory" : "none",
            scrollBehavior: "smooth",
            justifyContent: isTablet ? "flex-start" : "center",
            width: "100%",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {plans.map((plan, index) => (
            <Card
              key={index}
              sx={{
                flex: "0 0 auto",
                width: 300,
                minHeight: 520,
                maxHeight: 560,
                scrollSnapAlign: "center",
                border: "2px solid #000",
                borderRadius: "12px",
                boxShadow: "none",
                backgroundColor: index === 0 ? "#f1f5f9" : "#fff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2.5,
              }}
            >
              <Box>
                {plan.save ? (
                  <Box
                    sx={{
                      backgroundColor: "#d6f2c0",
                      color: "#76bc21",
                      fontSize: 12,
                      fontWeight: 600,
                      px: 1,
                      py: 0.5,
                      borderRadius: "4px",
                      display: "inline-block",
                      // mb: 1.5,
                    }}
                  >
                    {t("speed.save")} {plan.save}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      px: 1,
                      height: 24,
                      borderRadius: "4px",
                      display: "inline-block",
                      // mb: 1.5,
                    }}
                  ></Box>
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h5" fontWeight={600}>
                    {plan.name}
                  </Typography>
                  <Box
                    component="img"
                    src={plan.icon}
                    alt={plan.name}
                    sx={{ width: 100, height: "auto" }}
                  />
                </Box>
                <Box mt={2}>
                  {plan.features.map((feature, idx) => (
                    <Box key={idx} display="flex" alignItems="center" mb={1}>
                      <Box
                        component="img"
                        src={feature.icon}
                        alt={feature.label}
                        sx={{ width: 18, height: 18, mr: 1 }}
                      />
                      <Typography variant="body2" color="#1B2B39">
                        {feature.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    mt: 1,
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: 600,
                  }}
                  onClick={() => handleOpenDialog(plan)}
                >
                  {t("speed.viewDetails")}
                </Typography>
              </Box>
              <Box>
                <Divider sx={{ my: 1, borderStyle: "dashed" }} />
                <Typography
                  variant="body2"
                  color="#1B2B39"
                  fontWeight={500}
                  mt={2}
                >
                  {plan.price}
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  mt={1}
                  color="text.secondary"
                  sx={{
                    fontSize: 13,
                    backgroundColor: index < 2 ? "#d0f7b2ff" : "",
                    borderRadius: "4px",
                    maxWidth: "max-content",
                    px: 0.2,
                    fontWeight: 500,
                  }}
                >
                  {index < 2 && (
                    <LockOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  )}
                  {plan?.guarantee}
                </Box>
                {loadingPlanIndex === index ? (
                  <Grid
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "center",
                      minHeight: "36.5px",
                    }}
                  >
                    <BeatLoader color="#76bc21" />
                  </Grid>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleSelectPlan(plan, index)}
                    sx={{
                      backgroundColor: "#76bc21",
                      mt: 2,
                      color: "#1B2B39",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: "#76bc21" },
                    }}
                  >
                    {t("speed.selectPlan")}
                  </Button>
                )}
              </Box>
            </Card>
          ))}
        </Box>
        {isMobile && showNext && (
          <IconButton
            onClick={() => scroll("right")}
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              width: 32,
              height: 32,
              background: "rgba(255,255,255,0.9)",
              "&:hover": { background: "rgba(255,255,255,1)" },
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Dialog open={!!openDialog} onClose={handleCloseDialog} fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "24px !important",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          {openDialog?.name}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            size="small"
            sx={{
              ml: 1,
              color: (theme) => theme.palette.grey[600],
              display: "flex",
              alignItems: "center",
              borderRadius: "50%",
              width: 24,
              height: 24,
              border: "1px solid #ccc",
            }}
          >
            <CloseIcon fontSize="3px" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {Array.isArray(openDialog?.details) ? (
            openDialog.details.map((item, idx) => (
              <Box key={idx} mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.text}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {openDialog?.details}
            </Typography>
          )}
          <Grid container sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleCloseDialog}
            >
              {t("speed.close")}
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
      <ContinueButton
        submitting={submitting}
        goToBackStep={goToBackStep}
        continueButtonShow={false}
      />
    </Box>
  );
};

function mapStateToProps() {
  return {};
}

const mapDispatchToProps = {
  loadIsaEnrollmentCodeByState,
};

PricingPlans.propTypes = {
  setActiveStep: PropTypes.func,
  submitting: PropTypes.bool,
  goToBackStep: PropTypes.func,
  handleUpdate: PropTypes.func,
  validateCode: PropTypes.func,
  loadIsaEnrollmentCodeByState: PropTypes.func,
  onEnrollmentCodeError: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingPlans);
